import React, { useState, useEffect, useContext } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaBox,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const FinYearPendingTransaction = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass =
    "bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white";

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "8";
  const storedBranchType = localStorage.getItem("branchType") || "HO";
  const userId = localStorage.getItem("userId") || "";

  // common headers
  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchId,
    "x-branch": storedBranchType,
    "x-userid": userId,
  };

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [branchType, setBranchType] = useState(storedBranchType || "HO");
  const [branchId, setBranchId] = useState(storedBranchId || "8");
  const [branchList, setBranchList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [projectCode, setProjectCode] = useState("");
  const [supplier, setSupplier] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    setLoading(true);

    axios
      .get("https://nissi-india.com/api/dropdown_finRpt", {
        params: {
          branchid: branchId || storedBranchId || "8",
          branchtype: branchType || storedBranchType || "HO",
        },
        headers: commonHeaders,
      })
      .then((res) => {
        const stores =
          res.data.branches?.list ||
          res.data.branches ||
          res.data.data?.branches ||
          [];
        const suppliers =
          res.data.suppliers ||
          res.data.data?.suppliers ||
          [];

        setBranchList(stores);
        setSupplierList(suppliers);

        if (!branchId && stores.length > 0) {
          setBranchId(
            String(
              stores[0].id ||
                stores[0].fld_Branchid ||
                stores[0].value ||
                storedBranchId ||
                "8"
            )
          );
        }
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load dropdowns");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [branchType]);

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      finyrname: finYear || "2022-2023",
      branch: branchId || storedBranchId || "8",
      supplier: supplier || "%",
      type: projectCode || "%",
    };

    axios
      .get("https://nissi-india.com/api/finPendingTrans_listRpt", {
        params: payload,
        headers: commonHeaders,
      })
      .then((res) => {
        const rows =
          res.data.records ||
          res.data.data ||
          res.data.rows ||
          res.data.recordset ||
          res.data.recordsets?.[0] ||
          [];

        setData(rows);
        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("Pending transaction fetch failed", err);
        toast.error("Failed to fetch pending transactions");
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExcelDownload = () => {
    const payload = {
      finyrname: finYear || "2022-2023",
      branch: branchId || storedBranchId || "8",
      supplier: supplier || "%",
      type: projectCode || "%",
    };

    axios
      .get("https://nissi-india.com/api/finPendingTrans_excel", {
        params: payload,
        headers: commonHeaders,
        responseType: "blob",
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "FinYearPendingTransactions.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Excel file downloaded successfully");
      })
      .catch((err) => {
        console.error("Excel download failed", err);
        toast.error("Failed to download Excel");
      });
  };

  const handlePrint = () => {
    const payload = {
      finyrname: finYear || "2022-2023",
      branch: branchId || storedBranchId || "8",
      supplier: supplier || "%",
      type: projectCode || "%",
    };

    const query = new URLSearchParams(payload).toString();
    window.open(`/inventory/FinYearPendingReport_Print?${query}`, "_blank");
  };

  const clearFilters = () => {
    setBranchId(storedBranchId || "8");
    setSupplier("");
    setProjectCode("");
    setData([]);
  };

  const totalAmount = data.reduce(
    (sum, item) =>
      sum +
      (parseFloat(
        item.fld_amount_New ||
          item.amount ||
          item.Amount ||
          item.totalamount ||
          0
      ) || 0),
    0
  );

  return (
    <LayoutWrapper>
      <HeaderBar title="FIN YEAR PENDING TRANSACTION" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              Financial Year Pending Transactions
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              View and manage pending transactions for the financial year
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
              onClick={handleExcelDownload}
              disabled={data.length === 0}
            >
              <FaFileExcel className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Financial Year
                </label>
                <input
                  type="text"
                  value={finYear || "2022-2023"}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Transaction Type
                </label>
                <select
                  value={projectCode}
                  onChange={(e) => setProjectCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                >
                  <option value="">All Types</option>
                  <option value="P1">Purchase Request</option>
                  <option value="P2">Purchase Order</option>
                  <option value="Q">Quotation</option>
                  <option value="G">GRN</option>
                  <option value="B">Bill Pass</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Branch</label>
                <select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                >
                  {branchList.map((b) => (
                    <option
                      key={b.id || b.fld_Branchid || b.value}
                      value={b.id || b.fld_Branchid || b.value}
                    >
                      {b.name || b.fld_BranchName || b.text}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Supplier
                </label>
                <select
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                >
                  <option value="">All Suppliers</option>
                  {supplierList.map((s) => (
                    <option
                      key={s.id || s.fld_supplierid || s.value}
                      value={s.id || s.fld_supplierid || s.value}
                    >
                      {s.name || s.fld_suppliername || s.text}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={handleSearch}
                disabled={loading}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors disabled:opacity-50`}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaSearch className="w-4 h-4" />
                )}
                Search
              </button>
              <button
                onClick={handleExcelDownload}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaFileExcel className="w-4 h-4" />
                Export Excel
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} transactions)`}
            </h2>

            {data.length > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Transaction No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Amount (₹)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {data.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {item.fld_transaction || item.transactionType}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {item.fld_branchname || item.branchName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {item.fld_date || item.date}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {item.fld_transactionno || item.transactionNo}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                        {item.fld_suppliername || item.supplierName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right">
                        {parseFloat(
                          item.fld_amount_New || item.amount || 0
                        ).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                        {item.fld_notes || item.notes}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold">
                  <tr>
                    <td
                      className="px-4 py-4 text-sm text-gray-900 dark:text-white"
                      colSpan="6"
                    >
                      Total
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right">
                      {totalAmount.toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 dark:text-white"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
              <FaBox className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No pending transactions
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>

        {data.length > 0 && (
          <div
            className={`mt-6 p-5 rounded-lg ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <h3 className="text-lg font-semibold mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center">
                  {data.length}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Total Transactions
                </div>
              </div>
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center text-green-600 dark:text-green-400">
                  ₹
                  {totalAmount.toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Total Amount
                </div>
              </div>
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center text-blue-600 dark:text-blue-400">
                  ₹
                  {(totalAmount / data.length).toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Average per Transaction
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default FinYearPendingTransaction;